<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue';
import Compressor from 'compressorjs';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import NoImage from '../../../assets/images/no-image.png';

const props = defineProps({
  formNamespace: { type: String, default: undefined },
  imageField: { type: String, default: 'foto' },
  src: { type: String, default: '' },
  noImageSrc: { type: String, default: NoImage },
  deleting: { type: Boolean, default: false },
  canSave: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  width: { type: Number, default: 128 },
  height: { type: Number, default: 128 },
  // 'round', 'square', 'none'
  borderStyle: { type: String, default: 'round' },
  calidadImagen: { type: Number, default: 1 },
});

const emit = defineEmits(['save', 'modified']);

const $I18n = inject('I18n');

const cropper = ref(null);
const input = ref(null);
const flipX = ref(null);
const flipY = ref(null);
const cropImage = ref(null);

const imgSrc = ref('');
const cropImg = ref(props.src);
const data = ref(null);
const tipoMime = ref(null);
const showEditor = ref(false);
const modified = ref(false);
const deleteImage = ref(props.deleting);

const imageFieldInputName = computed(() => {
  return aplicarNamespace('image_field');
});

const imageInputFormName = computed(() => {
  return aplicarNamespace(props.imageField);
});

const deleteImageInputFormName = computed(() => {
  return aplicarNamespace(`delete_${props.imageField}`);
});

const aspectRatio = computed(() => {
  return props.width / props.height;
});

const sizeStyle = computed(() => {
  // Si no ponemos el height entonces el navegador recalcula el alto de la imagen
  // y al modificarla, la pone borrosa
  return `width: ${props.width}px; height: ${props.height}px; max-height: ${props.height}px;`;
});

const imgClasses = computed(() => {
  let classes = 'img-responsive';
  if (props.borderStyle === 'round') {
    classes = `${classes} img-thumbnail img-circle`;
  } else if (props.borderStyle === 'square') {
    classes = `${classes} img-thumbnail`;
  }
  return classes;
});

watch(cropImg, () => {
  modified.value = true;
});

watch(modified, (value) => {
  emit('modified', value);
});

onMounted(() => {
  if (cropImg.value.startsWith('data')) {
    cropImage.value.onload = () => {
      const canvas = document.createElement('canvas');
      // naturalWidth es el tamaño real de la imágen independientemente
      // de que en la web siempre se muestre a un tamaño de 128x128
      canvas.width = cropImage.value.naturalWidth;
      canvas.height = cropImage.value.naturalHeight;

      const context = canvas.getContext('2d');
      context.drawImage(cropImage.value, 0, 0);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], 'foto.jpeg');
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(resizedFile);
        input.value.files = dataTransfer.files;
      }, 'image/jpeg', props.calidadImagen);
    };
  }
});

function cropearImagen() {
  // get image data for post processing, e.g. upload or setting image src
  cropImg.value = cropper.value.getCroppedCanvas().toDataURL();
}

function flipXImagen() {
  const dom = flipX.value;
  let scale = dom.getAttribute('data-scale');
  scale = scale ? -scale : -1;
  cropper.value.scaleX(scale);
  dom.setAttribute('data-scale', scale);
}

function flipYImagen() {
  const dom = flipY.value;
  let scale = dom.getAttribute('data-scale');
  scale = scale ? -scale : -1;
  cropper.value.scaleY(scale);
  dom.setAttribute('data-scale', scale);
}

function getCropBoxData() {
  data.value = JSON.stringify(cropper.value.getCropBoxData(), null, 4);
}

function getData() {
  data.value = JSON.stringify(cropper.value.getData(), null, 4);
}

function move(offsetX, offsetY) {
  cropper.value.move(offsetX, offsetY);
}

function reset() {
  imgSrc.value = '';
  cropper.value.reset();
}

function rotate(deg) {
  cropper.value.rotate(deg);
}

function setCropBoxData() {
  if (!data.value) return;
  cropper.value.setCropBoxData(JSON.parse(data.value));
}

function setData() {
  if (!data.value) return;
  cropper.value.setData(JSON.parse(data.value));
}

function getImage() {
  return input.value.files[0];
}

function setImage(e) {
  const file = e.target.files[0];
  if (file.type.indexOf('image/') === -1) {
    // TODO: Gestión de errores
    alert('Debe seleccionar un fichero de tipo imagen');
    return;
  }

  tipoMime.value = file.type;

  // hay que mostrarlo antes porque si no no existe cropper y no podemos asignarle la imagen
  showEditor.value = true;
  convertirImagen(file, (imageObjectUrl) => {
    imgSrc.value = imageObjectUrl;
    cropper.value.replace(imageObjectUrl);
  });
}

function showFileChooser() {
  input.value.click();
}

function zoom(percent) {
  cropper.value.relativeZoom(percent);
}

function guardar() {
  cropearImagen();

  const tipo = tipoMime.value || 'image/jpeg';
  const extension = tipo.includes('/') ? tipo.split('/')[1] : 'jpeg';
  const nombreFichero = `foto.${extension}`;

  cropper.value.getCroppedCanvas().toBlob(
    (blob) => {
      const resizedFile = new File([blob], nombreFichero, { type: tipo });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(resizedFile);
      input.value.files = dataTransfer.files;

      deleteImage.value = false;
      showEditor.value = false;
      reset();
    },
    tipo,
    props.calidadImagen,
  );
}

function removeImage() {
  cropImg.value = '';
  input.value.value = '';
  deleteImage.value = true;
}

function aplicarNamespace(campo) {
  if (props.formNamespace) {
    return `${props.formNamespace}[${campo}]`;
  }
  return campo;
}

// Si seleccionan una imagen muy grande, la transformamos antes de cargarla
// en el selector cropper
function convertirImagen(fileOrBlob, callback) {
  const compressor = new Compressor(fileOrBlob, {
    quality: props.calidadImagen,
    maxWidth: 768,
    maxHeight: 768,
    success: (result) => {
      const imageObjectUrl = URL.createObjectURL(result);
      callback(imageObjectUrl);
    },
    error(err) {
      // TODO: Gestión de errores ¿Emitimos evento?
      alert(err.message);
    },
  });
}

defineExpose({
  getImage,
  cropImage: cropearImagen,
});
</script>

<template>
  <div :class="{ disabled: disabled }">
    <div class="cropped-image-wrapper">
      <div class="cropped-image" @click.prevent="showFileChooser">
        <template v-if="cropImg">
          <img
            ref="cropImage"
            :src="cropImg"
            alt="........"
            :class="imgClasses"
            :style="sizeStyle"
          />
          <button
            v-if="!disabled"
            type="button"
            class="btn btn-xs btn-danger btn-circle btn-remove"
            :title="$I18n.t('image_inline_input.remove_title')"
            @click.stop.prevent="removeImage()"
          >
            <i class="fa fa-times"></i>
          </button>
        </template>
        <img
          v-else
          :src="noImageSrc"
          alt="........"
          class="crop-placeholder"
          :class="imgClasses"
          :style="sizeStyle"
          style="object-fit: cover;"
        />
        <button
          v-if="!disabled && canSave && modified"
          type="button"
          class="btn btn-xs btn-success btn-circle btn-save"
          :title="$I18n.t('image_inline_input.save_title')"
          @click.stop.prevent="$emit('save')"
        >
          <i class="fa fa-save"></i>
        </button>
        <div v-if="canSave && modified" class="label label-info label-modified">
          {{ $I18n.t('no_guardado') }}
        </div>
      </div>
    </div>

    <input
      type="hidden"
      :name="imageFieldInputName"
      :value="imageField"
      :disabled="disabled"
    >
    <input
      ref="input"
      type="file"
      accept="image/*"
      :name="imageInputFormName"
      :disabled="disabled"
      @change="setImage"
    />
    <input
      type="hidden"
      :name="deleteImageInputFormName"
      :value="deleteImage"
      :disabled="disabled"
    >

    <div v-if="showEditor" class="content">
      <div class="modal-mask">
        <div class="modal-wrapper">
          <section class="card">
            <div class="card-body">
              <div class="img-cropper">
                <vue-cropper
                  ref="cropper"
                  :aspect-ratio="aspectRatio"
                  :src="imgSrc"
                  :auto-crop-area="1"
                  :view-mode="0"
                />
              </div>
            </div>
            <div class="card-footer">
              <div>
                <button class="btn btn-default" @click.prevent="zoom(0.2)">
                  <i class="fa fa-search-plus"></i>
                </button>
                <button class="btn btn-default" @click.prevent="zoom(-0.2)">
                  <i class="fa fa-search-minus"></i>
                </button>
                <button class="btn btn-default" @click.prevent="rotate(90)">
                  <i class="fa fa-redo"></i>
                </button>
                <button class="btn btn-default" @click.prevent="rotate(-90)">
                  <i class="fa fa-undo"></i>
                </button>
                <button
                  ref="flipX"
                  class="btn btn-default"
                  @click.prevent="flipXImagen"
                >
                  <i class="fa fa-arrows-h"></i>
                </button>
                <button
                  ref="flipY"
                  class="btn btn-default"
                  @click.prevent="flipYImagen"
                >
                  <i class="fa fa-arrows-v"></i>
                </button>
                <button class="btn btn-danger" @click.prevent="reset">
                  <i class="fa fa-sync"></i>
                </button>
              </div>
              <div class="form-actions">
                <button
                  class="btn btn-default"
                  @click.prevent="showEditor = false"
                >
                  {{ $I18n.t('Cancelar') }}
                </button>
                <button class="btn btn-cifra" @click.prevent="guardar">
                  {{ $I18n.t('Aceptar') }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
input[type="file"] {
  display: none;
}
.cropped-image-wrapper {
  text-align: center;
}
.cropped-image {
  position: relative;
  display: inline-block;

  img {
    // Para que los 128px sea los de la imagen sin contar el borde del img-thumbnail
    box-sizing: content-box;
    max-width: 100%;
  }
}
.card {
  max-width: 90%;
  max-height: 700px;
  margin: auto;
}
// css para que sea un modal
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}
.label-modified {
  position: absolute;
  right: 0;
  bottom: 5px;
}
.btn-remove {
  display: none;
  position: absolute;
  right: 0;
  top: 5px;
}
.btn-save {
  position: absolute;
  left: 0;
  bottom: 0;
}
.cropped-image {
  cursor: pointer;

  .disabled & {
    cursor: initial;
  }

  &:hover {
    .btn-edit,
    .btn-remove {
      &:not([disabled]) {
        display: block;
      }
    }
  }
}
</style>

